import React, { forwardRef, useCallback, useMemo } from 'react';
import { Command } from '../cmd-k/CmdK';
import { GenericItemBody } from '../dropdown-menu/DropdownMenuItemBody';
import type { ComboboxItemOnAction, ComboboxMenuItem, ComboboxProps } from './Combobox.types';

export const Combobox = forwardRef<HTMLDivElement, ComboboxProps>((props, forwardedRef) => {
  const { items = [], cmdInputProps, onSelect, itemComponent, ...cmdProps } = props;

  const itemComponents = useMemo(
    () =>
      items.map((item, index) => {
        if (item.type === 'separator') {
          return <Command.Separator key={`${item.type}-${index}`} />;
        }

        if (item.type === 'group') {
          return (
            <Command.Group key={item.label} heading={item.label}>
              {item.subOptions.map((subItem, subIndex) => {
                const key = 'value' in subItem ? `${subItem.value}-${subIndex}` : `sub-${subIndex}`;
                return (
                  <ComboboxItem
                    key={key}
                    item={subItem}
                    onSelect={onSelect}
                    itemComponent={itemComponent}
                  />
                );
              })}
            </Command.Group>
          );
        }

        return (
          <ComboboxItem
            key={`${item.value}-${index}`}
            item={item}
            onSelect={onSelect}
            itemComponent={itemComponent}
          />
        );
      }),
    [items, onSelect, itemComponent]
  );

  return (
    <Command {...cmdProps} ref={forwardedRef}>
      <Command.Input placeholder="Search for something..." autoFocus {...cmdInputProps} />
      {/* <DropdownMenuSeparatorComponent /> */}
      <Command.List>
        <Command.Empty>No results found.</Command.Empty>
        {itemComponents}
      </Command.List>
    </Command>
  );
});

export default Combobox;

type DropdownComboboxItemProps = {
  item: ComboboxMenuItem;
  onSelect?: ComboboxItemOnAction;
  itemComponent?: React.ComponentType<any>;
};

/**
 * cmdk interpolates each item's value, unescaped, into a `querySelector`
 * attribute selector (`[data-value="…"]`), so a `"` or `\` in the value throws
 * a SyntaxError and takes down the whole page. Without an explicit `value`
 * prop it falls back to the rendered text content — user-controlled data such
 * as integration project names. Filtering also matches against this value, so
 * keep it as close to the visible label as possible and only replace the
 * characters that can break the selector.
 */
export const toCmdkItemValue = (item: ComboboxMenuItem): string => {
  const label = 'label' in item && typeof item.label === 'string' ? item.label : undefined;
  const raw = label || ('value' in item ? item.value : '');
  return raw.replace(/["\\]/g, "'");
};

const ComboboxItem = ({ item, onSelect, itemComponent }: DropdownComboboxItemProps) => {
  const handleSelect = useCallback(() => {
    if (onSelect && 'value' in item) {
      onSelect({ value: item.value, item });
    }
  }, [item, onSelect]);

  const ItemContent = itemComponent || GenericItemBody;

  return (
    <Command.Item value={toCmdkItemValue(item)} onSelect={handleSelect}>
      <ItemContent item={item} />
    </Command.Item>
  );
};
